import { nextTick, ref, computed } from "vue";
import { defineStore } from "pinia";
import { useRouter } from "@library/Services";
import { useJupiterProps } from "@modules/Jupiter";
import { useUser } from "@domain/Security";
import { useUserSettings } from "@services/UserSettings";
import { useWelcomeFlowApi } from "./Types/Api/WelcomeFlowApi";
import { ICarbucksAppTile, IPanelLoadOptions, IWelcomeFlowConsumer, IWelcomeFlowVehicle, WelcomeFlowPanel } from "./Types/WelcomeFlow.types";
import { IUpsertVehicleRequest } from "./Types/Api/WelcomeFlowApiRequests";
import { ICompleteWelcomeFlowRequest } from "@app/Carbucks/Types/ICompleteWelcomeFlowRequest";
import { updateViewModelInstance } from "@app/MyGarage/Types/MyGarageViewModelStore";

export const useWelcomeFlowStore = defineStore("welcome-flow", () =>
{
    // --- Dependencies ---
    const _welcomeFlowApi = useWelcomeFlowApi();
    const _jupiterProps = useJupiterProps();

    // --- State ---
    const vehicle = ref<IWelcomeFlowVehicle>({ rego: undefined, regoState: undefined, odometer: undefined, app: {} });
    const consumer = ref<IWelcomeFlowConsumer>({
        consumerId: _jupiterProps.appConsumerId!,
        firstName: "",
        lastName: "",
        mobileNumber: "",
        mobileVerified: false,
    });

    // --- Error State ---
    interface ErrorState
    {
        title: string;
        subtitle: string | null;
        messageOne: string;
        messageTwo: string | null;
    }

    // this is the central state management for the error panel
    // the expectation is if you an api method encounters an error, you set the error state here and trigger a navigation to the error panel
    // the error panel itself will then be responsible for displaying the error message and clearing when navigation away
    const errorPanelState = ref<ErrorState>( {
        title: "",
        subtitle: "",
        messageOne: "",
        messageTwo: null
    } );

    const vehicleIsNotFound = ref<boolean>( false );
    const hasSearchedForVehicle = ref<boolean>( false );
    const originatorCode = ref<string>("");
    const availableApps = ref<ICarbucksAppTile[]>([]);
    const verificationCodeSent = ref<boolean>( false );
    const currentPanel = ref<WelcomeFlowPanel>( WelcomeFlowPanel.FindVehicle );
    const errorMessages = ref<string[]>([]);
    const loadingMessages = ref<string[]>([]);
    const completionAttempted = ref<boolean>( false );


    // --- Getters ---
    const hasMatch = computed( () => ( vehicle.value.vehicleId ?? "" ).length > 0 );
    const isFindVehicle = computed( () => currentPanel.value === WelcomeFlowPanel.FindVehicle );
    const vehicleDisplayName = computed( () =>
    {
        if( ( vehicle.value.make ?? vehicle.value.model ) == null ) return null;
        return `${vehicle.value.make ?? ""} ${vehicle.value.model ?? ""}`.trim();
    });

    // --- Actions ---

    /**
     * Initialises the welcome flow store by fetching the welcome flow data from the API
     * sets relevant state and navigates to the next panel based off of the data available
     */
    async function initialise()
    {
        
        await goto( WelcomeFlowPanel.LoadingPanel, { loadingMessages: [], minimumTime: 1000 } );

        const result = await _welcomeFlowApi.getWelcomeFlowDataAsync( _jupiterProps.appConsumerId!, _jupiterProps.appVehicleId! );
        if( !result.success )
        {
            errorMessages.value.push( result.messages as string );
            return;
        }

        if( result.data.vehicle )
        {
            vehicle.value = result.data.vehicle;
        }

        if( result.data.consumer )
        {
            consumer.value.firstName = result.data.consumer.firstName;
            consumer.value.lastName = result.data.consumer.lastName;
            consumer.value.mobileVerified = result.data.consumer.mobileVerified;
        }

        if( vehicle.value.vehicleId && vehicle.value.isEligibleToCompleteWelcomeFlow )
        {
            if( !completionAttempted.value )
            {
                await completeWelcomeFlowAsync();
                return;
            }

            errorPanelState.value = {
                title: "SORRY!",
                subtitle: null,
                messageOne: "Encountered an unexpected error. Please contact Carbucks Support",
                messageTwo: null
            };

            await goto( WelcomeFlowPanel.ErrorPanel );
            return;
        }

        await goto( WelcomeFlowPanel.FindVehicle );
        // await tryGotoNextPanel();
    }

    /**
     * Resets the search state and navigates you back to the find vehicle panel
     */
    function resetVehicleSearch()
    {
        vehicle.value = { app: {} };
        vehicleIsNotFound.value = false;
        hasSearchedForVehicle.value = false;
        completionAttempted.value = false;

        errorPanelState.value = {
            title: "",
            subtitle: "",
            messageOne: "",
            messageTwo: null
        };

        goto( WelcomeFlowPanel.FindVehicle );
    }

    /**
     * Moves to the given panel
     * @param panel The panel to navigate to
     * @param options Optional configuration for the transition
     */
    async function goto( newPanel: WelcomeFlowPanel, options?: IPanelLoadOptions )
    {
        const startTime = Date.now();
        
        // if we are going to the loading panel, set the messages
        if( newPanel === WelcomeFlowPanel.LoadingPanel )
        {
            loadingMessages.value = options?.loadingMessages ?? [];
            await nextTick();
        }
        // if we are leaving the loading panel for a final destination (not another loading state)
        else if( currentPanel.value === WelcomeFlowPanel.LoadingPanel )
        {
            loadingMessages.value = [];
            await nextTick();
        }
        
        currentPanel.value = newPanel;
        await nextTick();

        // if a minimum time is specified, wait for the minimum time
        if( newPanel === WelcomeFlowPanel.LoadingPanel && options?.minimumTime )
        {
            const elapsedTime = Date.now() - startTime;
            const minimumTime = options.minimumTime;

            if( elapsedTime < minimumTime )
            {
                await new Promise( resolve => setTimeout( resolve, minimumTime - elapsedTime ) );
            }
        }
    }

    /**
     * Retrieves vehicle details from the API and updates the store
     */
    async function getVehicleDetailsAsync()
    {
        // triggers the loading panel while we wait for the vehicle details
        await goto( WelcomeFlowPanel.LoadingPanel, {
            loadingMessages: [
                'Fetching your vehicle registration data',
                'Verifying your vehicle details with the Road Authority'
            ],
            minimumTime: 2000
        } );

        const getVehicleDetailsResult =
            await _welcomeFlowApi.getVehicleDetailsAsync(
                consumer.value.consumerId,
                vehicle.value.rego!,
                vehicle.value.regoState!
            );

        // update the search tracker to true
        hasSearchedForVehicle.value = true;

        // if the search was successful, update the model and set the not found flag to false
        if( !getVehicleDetailsResult.success )
        {
            // if the vehicle with the provided rego was not found in the platform DB
            // then set the error state to inform the user that the vehicle is not registered with Carbucks
            if( getVehicleDetailsResult.status === 404 )
            {
                errorPanelState.value = {
                    title: "SORRY!",
                    subtitle: "We can't find that car!",
                    messageOne: "Your vehicle may not have been setup yet. If you are at the dealership or service centre then ask a staff member to assist you",
                    messageTwo: "This is a great program for keeping your car healthy and saving you money, but it's only available from participating dealers"
                };

                await goto( WelcomeFlowPanel.ErrorPanel );
                return;
            }
        }

        // map the response data onto the vehicle model
        vehicle.value = getVehicleDetailsResult.data;
        if( !vehicle.value.isEligibleToCompleteWelcomeFlow )
        {
            errorPanelState.value = {
                title: "SORRY!",
                subtitle: null,
                messageOne: "Your PROTECT cover may not have been setup yet. If you are at the dealership or service centre then ask a staff member to assist you",
                messageTwo: "This is a great program for keeping your car healthy and saving you money, but it's only available from participating dealers"
            };

            await goto( WelcomeFlowPanel.ErrorPanel );
            return;
        }

        // this will trigger the load to end and the find vehicle panel to be displayed again
        await goto( WelcomeFlowPanel.FindVehicle );
    }

    /**
     * Saves the initial vehicle details (rego, state, odometer) and claims the vehicle.
     * Determines the next step based on whether an app needs to be selected.
     */
    async function claimVehicleAsync()
    {
        await goto( WelcomeFlowPanel.LoadingPanel, {
            loadingMessages: [ 'Saving your vehicle details' ],
            minimumTime: 1500
        } );

        const request: IUpsertVehicleRequest = {
            vehicleId: vehicle.value.vehicleId ?? null,
            consumerId: consumer.value.consumerId,
            appVehicleId: _jupiterProps.appVehicleId!,
            rego: vehicle.value.rego!,
            regoState: vehicle.value.regoState!,
            odometer: Number(vehicle.value.odometer ?? 0)
        };

        const upsertVehicleResult = await _welcomeFlowApi.claimVehicleAsync( request );

        if( !upsertVehicleResult.success )
        {
            errorMessages.value.push( upsertVehicleResult.messages as string );

            await goto( WelcomeFlowPanel.FindVehicle );
            return;
        }

        vehicle.value.vehicleId = upsertVehicleResult.data.vehicleId;
        vehicle.value.appSelected = !upsertVehicleResult.data.consumerNeedsToSelectApp;

        // complete the welcome flow and navigate to the my garage page
        await completeWelcomeFlowAsync();
    }

    /**
     * Completes the welcome flow and navigates to the my garage page
     */
    async function completeWelcomeFlowAsync()
    {
        await goto( WelcomeFlowPanel.LoadingPanel, {
            loadingMessages: [
                'Please wait a moment',
                'We\'re getting your car ready!'
            ],
            minimumTime: 4000
        } );

        const request: ICompleteWelcomeFlowRequest =
        {
            vehicleId: vehicle.value.vehicleId,
            consumerId: consumer.value.consumerId!,
            consumerVehicleId: vehicle.value.vehicleId!,
        };

        const result = await _welcomeFlowApi.completeWelcomeFlowAsync( request );

        completionAttempted.value = true;

        if( result.status == 200 )
        {
            proceedToMyGarageAsync();
        }
        else
        {
            // reinitialise the store which will get the latest data from the API
            // this will push the user to an error panel if they try to complete the welcome flow again
            await initialise();

            // now we have the latest version of the data saved for the consumer/vehicle combination
            // attempt to navigate to the panel we deem to be appropriate
            // await tryGotoNextPanel();
        }
    }

    /**
     * Updates any relevant stores and navigates to the my garage page
     */
    async function proceedToMyGarageAsync()
    {
        const user = useUser()!;
        const settings = useUserSettings();
        const router = useRouter();

        settings.isPairing = false;
        settings.numberOfPairingAttempts = 0;
        settings.skipHealthMonitorReminderUntil = null;

        await _jupiterProps.authenticate( true );
        await updateViewModelInstance( consumer.value.consumerId!, vehicle.value.vehicleId! );

        user.activeVehicle.requiresWelcomeFlow = false;

        await nextTick();
        router.vueRouter.replace("/my-garage");
    }

    /**
     * allows for a manual retry of the complete welcome flow method
     * this would normally be prevented by the completionAttempted flag
     */
    async function retryCompleteWelcomeFlowAsync()
    {
        completionAttempted.value = false;
        await completeWelcomeFlowAsync();
    }

    return {
        // State
        vehicle,
        consumer,
        vehicleIsNotFound,
        hasSearchedForVehicle,
        originatorCode,
        availableApps,
        errorMessages,
        verificationCodeSent,
        loadingMessages,
        currentPanel,
        errorPanelState,
        completionAttempted,

        // Getters
        hasMatch,
        isFindVehicle,
        vehicleDisplayName,

        // Actions
        initialise,
        resetVehicleSearch,
        goto,
        getVehicleDetailsAsync,
        claimVehicleAsync,
        // submitOriginatorCodeAsync,
        // setAppConfigurationAsync,
        // updateConsumerDetailsAsync,
        // updateMobileAsync,
        // verifyMobileAsync,
        completeWelcomeFlowAsync,
        proceedToMyGarageAsync,
        retryCompleteWelcomeFlowAsync
        // tryGotoNextPanel
    };
});

